import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import { useQueryClient } from '@tanstack/react-query';
import { ContactPage } from '@mui/icons-material';
import { ReactComponent as ReleaseNotes } from '~/release.md';
import logo from '~/assets/img/header_logo.png';
import logoSmall from '~/assets/img/d_logo_white.svg';
import React from 'react';
import useMenu from '~/context/useMiniMenu';
import useNotifications from '~/context/Notifications';
import useSessionPlQuery from '~/hooks/queries/useSessionPlQuery';
import useSession from '~/hooks/useSession/useSession';
import useWindowSize from '~/hooks/useWindowSize';
import { sidebarBreakpoint } from '~/variables/breakpoints';
import CustomLink from '~/components/Commons/Link/Link';
import {
  Box,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useParams } from 'react-router';
import NavbarClientSelector from '~/components/Clients/NavbarClientSelector';
import SidebarLink from './Link';
import SidebarItemIcon from './ItemIcon';
import List from './List';
import AdminUserLinks from '../UserLinks';
import dialog from '../ActionConfirmDialog/useDialog';
import { getCollapseStates, SidebarRoute } from './utils';
import { mobileStyles, desktopStyles, logoLinkStyle, authLinkItemSx } from './styles';
import {
  accountMenuItems,
  getVisibleMenuItems,
  buildMenuItemPath,
  MenuItemConfig,
} from './UserMenuItems';
import { SidebarToggleIcon } from './SidebarToggle';

type SidebarState = {
  openAvatar: boolean;
  [key: string]: boolean;
};

const reducer = (oldState: SidebarState, newState: Partial<SidebarState>): SidebarState => {
  const result: SidebarState = { ...oldState };
  Object.keys(newState).forEach((key) => {
    const value = newState[key];
    if (value !== undefined && typeof value === 'boolean') {
      result[key] = value;
    }
  });
  return result;
};

// sub-components for mobile account menu
interface MobileMenuItemProps {
  item: MenuItemConfig;
  mobileMenuItemStyle: typeof mobileStyles.menuItem;
  path?: string;
  onLinkClick: () => void;
  onButtonClick: () => void;
}

function MobileMenuItem({
  item,
  mobileMenuItemStyle,
  path,
  onLinkClick,
  onButtonClick,
}: MobileMenuItemProps) {
  const IconComponent = item.icon;
  const isLink = item.pathTemplate && item.id !== 'releaseNotes' && item.id !== 'signOut';

  if (isLink && path) {
    return (
      <ListItem
        key={item.id}
        component={CustomLink}
        to={path}
        onClick={onLinkClick}
        sx={mobileMenuItemStyle}
      >
        <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}>
          <IconComponent fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: {
              fontSize: '14px',
              fontWeight: 500,
            },
          }}
        />
      </ListItem>
    );
  }

  return (
    <ListItem key={item.id} component="button" onClick={onButtonClick} sx={mobileMenuItemStyle}>
      <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}>
        <IconComponent fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        slotProps={{
          primary: {
            fontSize: '14px',
            fontWeight: 500,
          },
        }}
      />
    </ListItem>
  );
}

// sub-component for desktop header with logo
interface SidebarHeaderProps {
  effectiveSidebarOpen: boolean;
  isAuth?: boolean;
  isStaff?: boolean;
  onLogoClick: () => void;
}

function SidebarHeader({ effectiveSidebarOpen, isAuth, isStaff, onLogoClick }: SidebarHeaderProps) {
  return (
    <Box
      sx={{
        maxWidth: effectiveSidebarOpen ? '210px' : '220px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'dianomiGrey.main',
        flexShrink: 0,
        margin: effectiveSidebarOpen ? '10px' : '0px 10px 10px',
        paddingTop: effectiveSidebarOpen ? '0px' : '10px',
        paddingBottom: '10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'all 250ms ease-out',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: effectiveSidebarOpen && !isAuth ? 'space-between' : 'center',
          alignItems: 'center',
          paddingTop: effectiveSidebarOpen ? '10px' : '0px',
          marginBottom: effectiveSidebarOpen && !isAuth ? '20px' : '0px',
          width: '100%',
          marginLeft: effectiveSidebarOpen && !isAuth ? '15px' : '0px',
          paddingBottom: isAuth ? '10px' : '0px',
          opacity: effectiveSidebarOpen ? 1 : 0,
          maxHeight: effectiveSidebarOpen ? '60px' : '0px',
          overflow: 'hidden',
          transition:
            'opacity 200ms ease-out, max-height 250ms ease-out, margin 250ms ease-out, padding 250ms ease-out',
        }}
      >
        <CustomLink to="/" style={logoLinkStyle} onClick={onLogoClick}>
          <Box
            component="img"
            src={logo}
            alt="Dianomi"
            sx={{
              width: '125px',
              height: '20px',
              transition: 'all 300ms linear',
              verticalAlign: 'middle',
              border: '0',
              position: 'relative',
              marginRight: isAuth ? '0px' : '18px',
              filter: 'invert(1)',
            }}
          />
        </CustomLink>
      </Box>
      {/* Collapsed staff logo */}
      {isStaff && !effectiveSidebarOpen && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px 0px',
          }}
        >
          <CustomLink to="/" style={logoLinkStyle} onClick={onLogoClick}>
            <Box
              component="img"
              src={logoSmall}
              alt="Dianomi"
              sx={{
                width: '32px',
                height: '32px',
                filter: 'invert(1)',
              }}
            />
          </CustomLink>
        </Box>
      )}
      {!isAuth && !isStaff && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: effectiveSidebarOpen ? '0px 10px' : '0px',
            transition: 'padding 250ms ease-out',
          }}
        >
          <NavbarClientSelector sidebarOpen={effectiveSidebarOpen} />
        </Box>
      )}
    </Box>
  );
}

// sub-component for the toggle button that straddles the sidebar edge
interface SidebarToggleButtonProps {
  effectiveSidebarOpen: boolean;
  onMenuToggle: (e: React.MouseEvent) => void;
}

function SidebarToggleButton({ effectiveSidebarOpen, onMenuToggle }: SidebarToggleButtonProps) {
  return (
    <Box sx={desktopStyles.toggleButtonContainer(effectiveSidebarOpen)}>
      <Tooltip
        title={effectiveSidebarOpen ? 'Close Menu' : 'Open Menu'}
        placement={effectiveSidebarOpen ? 'bottom' : 'right'}
        slotProps={{
          popper: {
            sx: { zIndex: 4000 },
          },
        }}
      >
        <IconButton onClick={onMenuToggle} sx={desktopStyles.toggleButton(effectiveSidebarOpen)}>
          <SidebarToggleIcon
            direction={effectiveSidebarOpen ? 'left' : 'right'}
            size={effectiveSidebarOpen ? 26 : 24}
            strokeWidth={32}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// sub-component for auth links (Help Center, Contact Support)
interface AuthLinksProps {
  effectiveSidebarOpen: boolean;
}

function AuthLinks({ effectiveSidebarOpen }: AuthLinksProps) {
  return (
    <>
      <ListItem
        component="a"
        href="https://support.dianomi.com/"
        target="_blank"
        rel="noopener noreferrer"
        sx={authLinkItemSx}
      >
        <SidebarItemIcon icon="CaseStudy" sidebarOpen={effectiveSidebarOpen} />
        {effectiveSidebarOpen && (
          <ListItemText
            primary="Help Center"
            slotProps={{
              primary: {
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
              },
            }}
          />
        )}
      </ListItem>
      <ListItem
        component="a"
        href="https://support.dianomi.com/hc/en-gb/requests/new"
        target="_blank"
        rel="noopener noreferrer"
        sx={authLinkItemSx}
      >
        <ContactPage fontSize="small" sx={{ marginLeft: '8px', color: 'inherit' }} />
        {effectiveSidebarOpen && (
          <ListItemText
            primary="Contact Support"
            slotProps={{
              primary: {
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
              },
            }}
          />
        )}
      </ListItem>
    </>
  );
}

// sub-component for rendering sidebar navigation links
interface SidebarLinksProps {
  routes: SidebarRoute[];
  basePath: string;
  sidebarOpen: boolean;
  sessionAccountType: string;
  state: SidebarState;
  activeFlyoutPath: string | null;
  onCollapseLinkClick: (stateKey: string) => (e: React.MouseEvent) => void;
  onMenuClick: () => void;
  onFlyoutChange: (path: string | null) => void;
  level?: number;
  inFlyout?: boolean;
  isNavDataLoading?: boolean;
}

function SidebarLinks({
  routes,
  basePath,
  sidebarOpen,
  sessionAccountType,
  state,
  activeFlyoutPath,
  onCollapseLinkClick,
  onMenuClick,
  onFlyoutChange,
  level = 0,
  inFlyout = false,
  isNavDataLoading = false,
}: SidebarLinksProps) {
  return (
    <>
      {routes.map((prop) => {
        if (!prop.allowed) {
          return null;
        }
        if ('hidden' in prop && prop.hidden) {
          return null;
        }
        if ('accountType' in prop && prop.accountType && prop.accountType !== sessionAccountType) {
          return null;
        }
        const stateKey =
          prop.collapse && prop.state && typeof prop.state === 'string' ? prop.state : undefined;
        const isCollapseActive = stateKey ? state[stateKey] : false;
        const views =
          prop.collapse && Array.isArray(prop.views) ? (prop.views as SidebarRoute[]) : undefined;

        // flyout content for collapsed sidebar - renders nested SidebarLinks
        const flyoutContent =
          prop.collapse && views ? (
            <List collapse level={level + 1}>
              <SidebarLinks
                routes={views}
                basePath={basePath}
                sidebarOpen={sidebarOpen}
                sessionAccountType={sessionAccountType}
                state={state}
                activeFlyoutPath={activeFlyoutPath}
                onCollapseLinkClick={onCollapseLinkClick}
                onMenuClick={onMenuClick}
                onFlyoutChange={onFlyoutChange}
                level={level + 1}
                inFlyout
                isNavDataLoading={isNavDataLoading}
              />
            </List>
          ) : undefined;

        return (
          <SidebarLink
            key={prop.path}
            basePath={basePath}
            prop={prop}
            sidebarOpen={sidebarOpen}
            onNavLinkClick={stateKey ? onCollapseLinkClick(stateKey) : onMenuClick}
            hasCaret={!!prop.collapse}
            isCollapseActive={isCollapseActive}
            level={level}
            flyoutContent={flyoutContent}
            activeFlyoutPath={activeFlyoutPath}
            onFlyoutChange={onFlyoutChange}
            inFlyout={inFlyout}
            isNavDataLoading={isNavDataLoading}
          >
            {prop.collapse && views && stateKey && (
              <Collapse in={isCollapseActive} unmountOnExit>
                <List collapse level={level + 1}>
                  <SidebarLinks
                    routes={views}
                    basePath={basePath}
                    sidebarOpen={sidebarOpen}
                    sessionAccountType={sessionAccountType}
                    state={state}
                    activeFlyoutPath={activeFlyoutPath}
                    onCollapseLinkClick={onCollapseLinkClick}
                    onMenuClick={onMenuClick}
                    onFlyoutChange={onFlyoutChange}
                    level={level + 1}
                    inFlyout={inFlyout}
                    isNavDataLoading={isNavDataLoading}
                  />
                </List>
              </Collapse>
            )}
          </SidebarLink>
        );
      })}
    </>
  );
}

interface SidebarProps {
  routes: SidebarRoute[];
  basePath: string;
  handleDrawerToggle: () => void;
  sidebarMinimize: () => void;
  isAuth?: boolean;
  isStaff?: boolean;
  isNavDataLoading?: boolean;
}

function Sidebar({
  routes,
  basePath,
  handleDrawerToggle,
  sidebarMinimize,
  isAuth,
  isStaff,
  isNavDataLoading,
}: SidebarProps) {
  const queryClient = useQueryClient();
  const { setNotification, closeNotification } = useNotifications();
  const { isOpen, closeMenu } = useMenu();
  const { isMobile } = useWindowSize();
  const { data: sessionData } = useSessionPlQuery();
  const { logout } = useSession();
  const location = useLocation();
  const { accountType, accountId } = useParams();
  const sessionAccountType = sessionData?.isPartner ? 'partner' : 'client';
  const collapseStates = getCollapseStates(routes);
  const initialState: SidebarState = {
    openAvatar: false,
    ...collapseStates,
  } as SidebarState;
  const [state, setState] = React.useReducer(reducer, initialState);
  const [activeFlyoutPath, setActiveFlyoutPath] = React.useState<string | null>(null);

  const onCollapseLinkClick = (st: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const status = state[st];
    setState({ [st]: !status });
  };

  const onMenuClick = () => {
    if (window.innerWidth < sidebarBreakpoint) {
      closeMenu();
    }
  };

  const effectiveSidebarOpen = isAuth ? true : isOpen;

  const handleLogout = () => {
    closeMenu();
    logout(500);
  };

  const handleOpenReleaseNotes = () => {
    closeMenu();
    dialog({
      title: "What's New on MyDianomi",
      body: <ReleaseNotes />,
      cancelText: 'Close',
      width: 'md',
      onClose: () => dialog.close(),
      onOpen: null,
      showCloseButton: true,
    });
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleDrawerToggle();
  };

  // select desktop variant based on sidebar state
  const desktopVariant = effectiveSidebarOpen ? desktopStyles.expanded : desktopStyles.collapsed;

  const visibleMenuItems = getVisibleMenuItems(accountMenuItems, {
    isDianomi: !!sessionData?.login?.is_dianomi,
    isClientAccount: accountType === 'client',
    currentPath: location.pathname,
  });

  // mobile drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={isOpen}
        sx={mobileStyles.drawer}
        onClose={closeMenu}
        ModalProps={{
          keepMounted: false,
          BackdropProps: { style: { opacity: 0 } },
        }}
      >
        <Box sx={mobileStyles.sidebarWrapper}>
          {!isStaff && <NavbarClientSelector sidebarOpen={isOpen} onClientChange={closeMenu} />}
          <List>
            <SidebarLinks
              routes={routes}
              basePath={basePath}
              sidebarOpen={isOpen}
              sessionAccountType={sessionAccountType}
              state={state}
              activeFlyoutPath={activeFlyoutPath}
              onCollapseLinkClick={onCollapseLinkClick}
              onMenuClick={onMenuClick}
              onFlyoutChange={setActiveFlyoutPath}
              isNavDataLoading={isNavDataLoading}
            />
          </List>

          <Divider sx={mobileStyles.divider} />

          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="overline" sx={mobileStyles.accountHeader}>
              User - {sessionData?.login?.name}
            </Typography>
          </Box>

          <Box sx={{ px: 0 }}>
            {visibleMenuItems.map((item) => {
              const path =
                item.pathTemplate && item.id !== 'releaseNotes' && item.id !== 'signOut'
                  ? buildMenuItemPath(item.pathTemplate, accountType ?? '', accountId ?? '')
                  : undefined;

              const handleButtonClick = () => {
                if (item.id === 'releaseNotes') {
                  handleOpenReleaseNotes();
                } else if (item.id === 'signOut') {
                  handleLogout();
                } else {
                  closeMenu();
                }
              };

              return (
                <MobileMenuItem
                  key={item.id}
                  item={item}
                  mobileMenuItemStyle={mobileStyles.menuItem}
                  path={path}
                  onLinkClick={closeMenu}
                  onButtonClick={handleButtonClick}
                />
              );
            })}
          </Box>
        </Box>
      </Drawer>
    );
  }

  // desktop drawer
  return (
    <div>
      <Drawer anchor="left" variant="permanent" open sx={desktopVariant.drawer}>
        {/* Toggle button straddling the sidebar edge */}
        {!isAuth && (
          <SidebarToggleButton
            effectiveSidebarOpen={effectiveSidebarOpen}
            onMenuToggle={handleMenuToggle}
          />
        )}
        <Box sx={desktopVariant.sidebarWrapper}>
          <SidebarHeader
            effectiveSidebarOpen={effectiveSidebarOpen}
            isAuth={isAuth}
            isStaff={isStaff}
            onLogoClick={() => queryClient.clear()}
          />

          <Box sx={desktopStyles.scrollableArea}>
            <List sx={desktopStyles.list}>
              {isAuth ? (
                <AuthLinks effectiveSidebarOpen={effectiveSidebarOpen} />
              ) : (
                <SidebarLinks
                  routes={routes}
                  basePath={basePath}
                  sidebarOpen={isOpen}
                  sessionAccountType={sessionAccountType}
                  state={state}
                  activeFlyoutPath={activeFlyoutPath}
                  onCollapseLinkClick={onCollapseLinkClick}
                  onMenuClick={onMenuClick}
                  onFlyoutChange={setActiveFlyoutPath}
                  isNavDataLoading={isNavDataLoading}
                />
              )}
            </List>
          </Box>

          <Box sx={desktopStyles.footer}>
            <AdminUserLinks
              setNotification={setNotification}
              closeNotification={closeNotification}
              sidebarOpen={effectiveSidebarOpen}
              isAuth={isAuth}
            />
          </Box>
        </Box>
      </Drawer>
    </div>
  );
}

export default Sidebar;
